/**
 * Phase 2R-Creator — Creator badge strip.
 *
 * Renders the creator's earned badges as a quiet, low-contrast row beside
 * the uploader byline.  Design constraints (per CEO):
 *   - identity, not gamification — no level/XP/progress hints
 *   - badges appear "unexpectedly" — no "next badge" tease
 *   - blurb available on hover ONLY for context, never as a CTA
 *   - slow, calm aesthetic — never aggressive glows
 *
 * Loads lazily per uploader_id and caches in-component so a clip reel with
 * many uploaders never thrashes the API.
 */
import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';

const cache = new Map(); // uploader_id -> reputation

export default function ArenaxCreatorBadgeStrip({ uploaderId, uploaderName, compact = false }) {
  const [rep, setRep] = useState(() => cache.get(uploaderId) || null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!uploaderId) return;
    if (cache.has(uploaderId)) {
      setRep(cache.get(uploaderId));
      return;
    }
    let cancelled = false;
    api.get(`/arenax/creators/${encodeURIComponent(uploaderId)}/reputation`)
      .then(r => {
        if (cancelled || !mounted.current) return;
        cache.set(uploaderId, r.data);
        setRep(r.data);
      })
      .catch(() => {
        // Reputation is best-effort surface. Silent failure.
      });
    return () => { cancelled = true; mounted.current = false; };
  }, [uploaderId]);

  const badges = rep?.badges || [];
  if (!uploaderName && badges.length === 0) return null;

  if (compact) {
    // Compact: just the emoji row (used inline next to small bylines)
    if (badges.length === 0) return null;
    return (
      <span
        className="inline-flex items-center gap-0.5 ml-1"
        data-testid={`arenax-creator-badges-compact-${uploaderId}`}
      >
        {badges.slice(0, 3).map(b => (
          <span key={b.key} title={`${b.label} — ${b.blurb}`} className="text-[11px] leading-none">
            {b.emoji}
          </span>
        ))}
      </span>
    );
  }

  return (
    <div
      className="flex items-center gap-2 text-[10px] text-slate-400"
      data-testid={`arenax-creator-strip-${uploaderId}`}
    >
      {uploaderName && (
        <span className="font-medium text-slate-300 truncate max-w-[120px]">{uploaderName}</span>
      )}
      {badges.length > 0 && (
        <span className="opacity-50">·</span>
      )}
      {badges.slice(0, 2).map(b => (
        <span
          key={b.key}
          title={`${b.label} — ${b.blurb}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm"
          style={{
            background: 'rgba(148,163,184,0.10)',
            color: '#cbd5e1',
          }}
          data-testid={`arenax-creator-badge-${uploaderId}-${b.key}`}
        >
          <span className="text-[11px] leading-none">{b.emoji}</span>
          <span className="text-[10px] tracking-wide">{b.label}</span>
        </span>
      ))}
    </div>
  );
}
